import { BaseValidator } from './baseValidator';
import { ValidatorResult } from './validatorRule.constants';
import { CardHolderNameValidationMessages } from './cardHolderNameValidationMessages';

export class CardHolderNameValidator extends BaseValidator {
    private readonly MIN_LENGTH = 2;
    private readonly MAX_LENGTH = 42;

    constructor() {
        super([]);
    }

    public get maxLength(): number {
        return this.MAX_LENGTH;
    }

    /**
     * Validates the cardholder name and returns ALL validation errors
     * This allows multiple errors to be displayed simultaneously without priority ordering
     */
    public validateAll(value: string, fieldName?: string, region?: string): ValidatorResult[] {
        const errors: ValidatorResult[] = [];

        if (this.isBlankValue(value)) {
            errors.push(CardHolderNameValidationMessages.createBlankError(value, region));
            return errors;
        }

        const trimmedValue = value.trim();

        if (this.isTooShort(trimmedValue)) {
            errors.push(
                CardHolderNameValidationMessages.createTooShortError(value, this.MIN_LENGTH),
            );
        }

        if (this.isTooLong(trimmedValue)) {
            errors.push(
                CardHolderNameValidationMessages.createTooLongError(value, this.MAX_LENGTH),
            );
        }

        const uppercaseValue = trimmedValue.toUpperCase();

        if (this.hasInvalidSymbols(uppercaseValue)) {
            errors.push(CardHolderNameValidationMessages.createInvalidSymbolsError(value));
        }

        const hasPAN = this.detectAnyPAN(trimmedValue, uppercaseValue);
        if (hasPAN) {
            errors.push(CardHolderNameValidationMessages.createPanError(value));
        }

        if (this.hasInvalidFormat(uppercaseValue)) {
            errors.push(CardHolderNameValidationMessages.createInvalidFormatError(value, region));
        }

        return errors;
    }

    /**
     * Consolidated PAN detection that checks all PAN patterns
     * Returns true if any PAN pattern is detected
     */
    private detectAnyPAN(trimmedValue: string, uppercaseValue: string): boolean {
        // Check 1: Primary PAN check (Luhn algorithm)
        const panCheckResult = this.checkForPAN(trimmedValue);
        if (panCheckResult !== null) {
            return true;
        }

        // Check 2: 12+ consecutive digits (ignoring spaces/dashes)
        const valueWithoutSpaces = uppercaseValue.replace(/[\s\-]/g, '');
        if (this.hasConsecutiveDigits(valueWithoutSpaces, 12)) {
            return true;
        }

        // Check 3: 4-4-4-4 card number pattern anywhere in the string
        if (/\d{4}[\s\-]?\d{4}[\s\-]?\d{4}[\s\-]?\d{4}/.test(uppercaseValue)) {
            return true;
        }

        // Check 4: Input looks like a PAN entry pattern (4-4-4 digits at start)
        if (this.looksLikePanEntry(uppercaseValue)) {
            return true;
        }

        return false;
    }

    // Validation patterns
    private readonly STRICT_MODE_CHARS = /^[A-Z\s.\-']*$/;

    // PAN detection patterns
    private readonly PAN_WITH_SPACES = /^\d{4}[\s\-]?\d{4}[\s\-]?\d{4}[\s\-]?\d{3,4}$/;
    private readonly PAN_CONTINUOUS = /^\d{13,19}$/;
    private readonly PAN_WITH_TRAILING_CHAR = /^\d{15,16}[a-zA-Z]$/;

    /**
     * Validates the cardholder name and returns the first error found (legacy behavior)
     * For backward compatibility with existing code; use validateAll() when multiple errors are needed
     */
    public validate(value: string, fieldName?: string, region?: string): ValidatorResult {
        const errors = this.validateAll(value, fieldName, region);
        return errors.length > 0 ? errors[0] : null;
    }

    private isBlankValue(value: string): boolean {
        return !value || !value.trim();
    }

    private isTooShort(trimmedValue: string): boolean {
        return trimmedValue.length < this.MIN_LENGTH;
    }

    private isTooLong(trimmedValue: string): boolean {
        return trimmedValue.length > this.MAX_LENGTH;
    }

    private hasInvalidSymbols(uppercaseValue: string): boolean {
        // Check for specific invalid symbols that are always blocked (error level)
        // Blocked symbols: < > & " ? # % ^ * \ / { } ( |
        // These are not allowed in cardholder names per business requirements
        const invalidSymbols = /[<>&"?#%^*\\\/{}(|]/;
        return invalidSymbols.test(uppercaseValue);
    }

    private hasInvalidFormat(uppercaseValue: string): boolean {
        let convertedValue = uppercaseValue;

        convertedValue = convertedValue.replace(/[\uFF01-\uFF5E]/g, (char) => {
            return String.fromCharCode(char.charCodeAt(0) - 0xfee0);
        });

        convertedValue = convertedValue.replace(/\u3000/g, ' ');

        return /[^\x20-\x7E]/.test(convertedValue);
    }

    public getWarning(value: string): string | null {
        if (this.isBlankValue(value)) {
            return null;
        }

        const uppercaseValue = value.trim().toUpperCase();

        if (this.containsNumbersOrUnusualSymbols(uppercaseValue)) {
            return CardHolderNameValidationMessages.WARNING_KEYS.CONTAINS_NUMBERS_OR_SYMBOLS;
        }

        return null;
    }

    private looksLikePanEntry(uppercaseValue: string): boolean {
        return /^\d{4}[\s\-]?\d{4}[\s\-]?\d{4}/.test(uppercaseValue);
    }

    private containsNumbersOrUnusualSymbols(uppercaseValue: string): boolean {
        if (/\d/.test(uppercaseValue)) {
            return true;
        }

        // Check for unusual symbols that trigger warnings (not errors)
        // Excludes: < > & " ? # % ^ * \ / { } ( | (these are error-level invalid symbols)
        // Includes: @ ! $ + = [ ] ; ~ ` _ (these trigger warnings only)
        const unusualSymbols = /[@!$+=\[\];~`_]/;
        return unusualSymbols.test(uppercaseValue);
    }

    private hasConsecutiveDigits(value: string, minCount: number): boolean {
        const consecutiveDigitsPattern = new RegExp(`\\d{${minCount},}`);
        return consecutiveDigitsPattern.test(value);
    }

    private checkForPAN(value: string): ValidatorResult {
        const digitsOnly = value.replace(/\D/g, '');

        if (this.isPotentialPan(value, digitsOnly)) {
            return CardHolderNameValidationMessages.createPanError(value);
        }

        const fullWidthConverted = this.convertFullWidthDigits(value);
        if (this.hasFullWidthPan(value, fullWidthConverted)) {
            return CardHolderNameValidationMessages.createPanError(value);
        }

        return null;
    }

    private isPotentialPan(value: string, digitsOnly: string): boolean {
        return (
            this.isValidPanLength(digitsOnly) &&
            this.matchesPanPattern(value, digitsOnly) &&
            this.isValidLuhn(digitsOnly)
        );
    }

    private isValidPanLength(digitsOnly: string): boolean {
        return digitsOnly.length >= 13 && digitsOnly.length <= 19;
    }

    private matchesPanPattern(value: string, digitsOnly: string): boolean {
        // PAN_WITH_SPACES and PAN_WITH_TRAILING_CHAR check the original value format
        if (this.PAN_WITH_SPACES.test(value) || this.PAN_WITH_TRAILING_CHAR.test(value)) {
            return true;
        }

        // PAN_CONTINUOUS checks stripped digits
        if (this.PAN_CONTINUOUS.test(digitsOnly)) {
            return true;
        }

        return false;
    }

    private convertFullWidthDigits(value: string): string {
        return value.replace(/[０-９]/g, (match) => {
            return String.fromCharCode(match.charCodeAt(0) - 0xfee0);
        });
    }

    private hasFullWidthPan(originalValue: string, convertedValue: string): boolean {
        if (convertedValue === originalValue) {
            return false;
        }
        const convertedDigitsOnly = convertedValue.replace(/\D/g, '');
        return convertedDigitsOnly.length >= 13 && this.isValidLuhn(convertedDigitsOnly);
    }

    private isValidLuhn(digits: string): boolean {
        if (digits.length < 13 || digits.length > 19) {
            return false;
        }

        let sum = 0;
        let isEven = false;

        for (let i = digits.length - 1; i >= 0; i--) {
            let digit = parseInt(digits.charAt(i), 10);

            if (isEven) {
                digit *= 2;
                if (digit > 9) {
                    digit -= 9;
                }
            }

            sum += digit;
            isEven = !isEven;
        }

        return sum % 10 === 0;
    }

    /**
     * Strict validation mode - more restrictive than standard validation
     * Currently not used in production, available for future use cases
     *
     * Differences from standard validation:
     * - Rejects numbers (standard allows them with warning)
     * - Rejects unusual symbols like @ (standard allows them with warning)
     * - Only allows: letters, spaces, apostrophes, hyphens, periods
     */
    public validateStrict(value: string): ValidatorResult {
        const basicResult = this.validate(value);
        if (basicResult !== null) {
            return basicResult;
        }

        const uppercaseValue = value.trim().toUpperCase();

        if (this.containsNumbers(uppercaseValue)) {
            return CardHolderNameValidationMessages.createNumbersOrSymbolsWarning(value);
        }

        if (this.hasDisallowedCharsInStrictMode(uppercaseValue)) {
            return CardHolderNameValidationMessages.createNoSpecialCharsError(value);
        }

        return null;
    }

    private containsNumbers(uppercaseValue: string): boolean {
        return /\d/.test(uppercaseValue);
    }

    private hasDisallowedCharsInStrictMode(uppercaseValue: string): boolean {
        return !this.STRICT_MODE_CHARS.test(uppercaseValue);
    }
}
